import { Descriptions } from "@/components/v2";
import {
  evaluateCarClassification,
  getClassificationAmount,
} from "@/constants/car-angle-classification";
import {
  getMediaIntentLabel,
  getMediaIntentValue,
  type MediaIntent,
} from "@/constants/media-intent-labels";
import { Badge, Image, Space, Tag, Tooltip, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { MediaUsageMetadata } from "@rumble-it/common-lib/types/models/MediaUsageMetadata.interface";
import { Card } from "@rumbledirect/ui";
import { ClassificationTooltipContent } from "./classification-tooltip";

const { Text } = Typography;

interface MediaCardProps {
  dossier: Dossier | undefined;
  isLoading: boolean;
}

export const MediaCard = ({ dossier, isLoading }: MediaCardProps) => {
  type NormalizedMediaUsage = {
    id: string | number;
    intent: MediaIntent;
    metadata?: MediaUsageMetadata | null;
    media: {
      id?: string | number;
      contentUrl: string;
      mimeType?: string;
    };
  };

  const mediaUsages = dossier?.mediaUsages || [];

  const normalizedMediaUsages: NormalizedMediaUsage[] = mediaUsages.flatMap(
    (usage, idx) => {
      if (!usage?.media?.contentUrl) {
        return [];
      }

      return [
        {
          id: usage.id ?? `media-usage-${idx}`,
          intent: usage.intent ?? "media",
          metadata: usage.metadata,
          media: {
            id: usage.media.id,
            contentUrl: usage.media.contentUrl,
            mimeType: usage.media.mimeType,
          },
        },
      ];
    },
  );

  // ImageCapture images (first batch only, mirroring show.jsx logic)
  const imageCaptureMediaUsages: NormalizedMediaUsage[] = (
    dossier?.imageCaptureSents?.[0]?.imageCaptureMedia || []
  ).flatMap((imageCaptureItem, idx) => {
    if (
      !imageCaptureItem?.media?.contentUrl ||
      !imageCaptureItem.media?.mimeType?.startsWith("image/")
    ) {
      return [];
    }

    return [
      {
        id: `image-capture-${imageCaptureItem.media.id ?? idx}`,
        intent: imageCaptureItem.intent ?? "image_capture",
        metadata: imageCaptureItem.metadata,
        media: {
          id: imageCaptureItem.media.id,
          contentUrl: imageCaptureItem.media.contentUrl,
          mimeType: imageCaptureItem.media.mimeType,
        },
      },
    ];
  });

  const allMediaUsages = [...normalizedMediaUsages, ...imageCaptureMediaUsages];

  const seenMediaKeys = new Set<string>();
  const uniqueMediaUsages = allMediaUsages.filter((usage, idx) => {
    const mediaKey = String(
      usage.media.id ||
        usage.media.contentUrl ||
        `${usage.media.mimeType || "unknown"}-${idx}`,
    );

    if (seenMediaKeys.has(mediaKey)) {
      return false;
    }

    seenMediaKeys.add(mediaKey);
    return true;
  });

  const mediaUsagesByIntent = uniqueMediaUsages.reduce(
    (grouped, mediaUsage) => {
      const intentLabel = getMediaIntentLabel(mediaUsage.intent);
      const intentMedia = grouped.get(intentLabel) ?? [];
      intentMedia.push(mediaUsage);
      grouped.set(intentLabel, intentMedia);
      return grouped;
    },
    new Map<string, NormalizedMediaUsage[]>(),
  );

  return (
    <Card loading={isLoading} className="w-full" title="Afbeeldingen">
      {uniqueMediaUsages.length === 0 ? (
        <Text type="secondary">Geen afbeeldingen beschikbaar</Text>
      ) : (
        <Image.PreviewGroup>
          <Descriptions
            items={[
              ...Array.from(mediaUsagesByIntent.entries()).map(
                ([intentLabel, intentMediaUsages]) => ({
                  key: intentLabel,
                  label: intentLabel,
                  children: (
                    <Space
                      align="start"
                      size="middle"
                      wrap
                      style={{ width: "100%" }}
                    >
                      {intentMediaUsages.map((mediaUsage, mediaIndex) => {
                        if (!mediaUsage.media.mimeType?.startsWith("image/")) {
                          return (
                            <Space
                              key={`${mediaUsage.id}-${mediaIndex}`}
                              orientation="horizontal"
                              align="center"
                            >
                              <a
                                href={mediaUsage.media.contentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex gap-2 text-primary-default"
                              >
                                <DownloadOutlined
                                  style={{ fontSize: 16, color: "#999" }}
                                />
                                <span>Bestand downloaden</span>
                              </a>
                            </Space>
                          );
                        }

                        // Evaluate the image against the AI agent's
                        // classification (is-car + expected angle + quality).
                        // A border is only shown once the agent has classified
                        // a car-angle image.
                        const evaluation = evaluateCarClassification(
                          getMediaIntentValue(mediaUsage.intent),
                          mediaUsage.metadata,
                        );
                        const isRejected = evaluation.status === "rejected";
                        const isApproved = evaluation.status === "approved";
                        const showBorder = isRejected || isApproved;
                        const borderColor = isRejected ? "red" : "green";

                        // The approved classifications and the deviations for
                        // this image, plus how many were approved out of the
                        // total number of classifications performed.
                        const classificationAmount = getClassificationAmount(
                          mediaUsage.metadata,
                        );
                        const approvals =
                          evaluation.status === "not-evaluated"
                            ? []
                            : evaluation.approvals;
                        const problems =
                          evaluation.status === "not-evaluated"
                            ? []
                            : evaluation.problems;
                        const approvedCount = approvals.length;

                        const tooltipTitle = showBorder ? (
                          <ClassificationTooltipContent
                            approvals={approvals}
                            problems={problems}
                          />
                        ) : undefined;

                        const image = (
                          <Image
                            key={`${mediaUsage.id}-${mediaIndex}`}
                            src={mediaUsage.media.contentUrl}
                            alt={`${intentLabel} - ${
                              mediaUsage.media.contentUrl
                                .split("/")
                                .pop()
                                ?.split("?")[0] ?? "image"
                            }`}
                            style={{
                              width: 180,
                              maxWidth: "100%",
                              maxHeight: 200,
                              borderRadius: 4,
                              border: showBorder
                                ? `5px solid ${borderColor}`
                                : undefined,
                              opacity: isRejected ? 0.8 : 1,
                            }}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                          />
                        );

                        return (
                          <Space
                            key={`${mediaUsage.id}-${mediaIndex}`}
                            orientation="vertical"
                            size={4}
                            align="center"
                          >
                            {showBorder ? (
                              <Badge
                                count={`${approvedCount}/${classificationAmount}`}
                                color={
                                  approvedCount === classificationAmount
                                    ? "green"
                                    : "red"
                                }
                                showZero
                                title={`${approvedCount} van de ${classificationAmount} AI-classificaties goedgekeurd`}
                              >
                                {tooltipTitle ? (
                                  <Tooltip title={tooltipTitle}>
                                    {/* antd's Image does not forward a ref to a DOM
                                        node, so Tooltip cannot anchor to it and
                                        renders off-screen. Wrap it in an inline-block
                                        span that Tooltip can position against. */}
                                    <span
                                      style={{
                                        display: "inline-block",
                                        lineHeight: 0,
                                      }}
                                    >
                                      {image}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  image
                                )}
                              </Badge>
                            ) : (
                              image
                            )}
                            {isRejected ? (
                              <Tag color="red">Afwijkend</Tag>
                            ) : null}
                          </Space>
                        );
                      })}
                    </Space>
                  ),
                }),
              ),
            ]}
          />
        </Image.PreviewGroup>
      )}
    </Card>
  );
};
